/**
 * Portal Vouchers Page
 * Voucher management for business users — CRUD + stats + sales history.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import vouchersService, {
  PortalVoucher,
  PortalVoucherInput,
  PortalVoucherStats,
  PortalVoucherVenda,
  VoucherTipo,
} from '../../services/vouchers.service';

type FormState = PortalVoucherInput & { id?: number };

const emptyForm: FormState = {
  tipo_id: 0,
  nome_entidade: '',
  titulo: '',
  descricao: '',
  valor: null,
  percentagem_desconto: null,
  valor_minimo_compra: null,
  quantidade_total: 1,
  preco_venda: 0,
  data_validade: null,
  termos_uso: '',
  imagem_logo: '',
  status: 'rascunho',
};

const PortalVouchers: React.FC = () => {
  const [vouchers, setVouchers] = useState<PortalVoucher[]>([]);
  const [tipos, setTipos] = useState<VoucherTipo[]>([]);
  const [stats, setStats] = useState<PortalVoucherStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [vendasVoucher, setVendasVoucher] = useState<PortalVoucher | null>(null);
  const [vendas, setVendas] = useState<PortalVoucherVenda[]>([]);

  // Tipo currently selected in the form, to know if it's value or discount
  const tipoSelecionado = useMemo(
    () => tipos.find((t) => t.id === form.tipo_id) || null,
    [form.tipo_id, tipos]
  );

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, tiposRes, statsRes] = await Promise.all([
        vouchersService.portalListVouchers(statusFilter ? { status: statusFilter } : undefined),
        vouchersService.portalGetTipos(),
        vouchersService.portalGetStats(),
      ]);
      setVouchers(list);
      setTipos(tiposRes);
      setStats(statsRes);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (v: PortalVoucher) => {
    setForm({
      id: v.id,
      tipo_id: v.tipo_id,
      nome_entidade: (v as any).nome_entidade || '',
      titulo: v.titulo,
      descricao: v.descricao || '',
      valor: v.valor,
      percentagem_desconto: v.percentagem_desconto,
      valor_minimo_compra: v.valor_minimo_compra,
      quantidade_total: v.quantidade_total,
      preco_venda: v.preco_venda,
      data_validade: v.data_validade ? v.data_validade.substring(0, 10) : null,
      termos_uso: v.termos_uso || '',
      imagem_logo: v.imagem_logo || '',
      status: v.status,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side pre-check: require exactly one of valor / percentagem_desconto
    const hasValor = form.valor !== null && form.valor !== undefined && Number(form.valor) > 0;
    const hasPerc =
      form.percentagem_desconto !== null &&
      form.percentagem_desconto !== undefined &&
      Number(form.percentagem_desconto) > 0;

    if (tipoSelecionado?.tipo === 'valor' && !hasValor) {
      setError('Para vouchers do tipo "valor" é obrigatório indicar o valor facial.');
      return;
    }
    if (tipoSelecionado?.tipo === 'desconto' && !hasPerc) {
      setError('Para vouchers do tipo "desconto" é obrigatório indicar a percentagem.');
      return;
    }
    if (hasValor && hasPerc) {
      setError('Indique apenas valor OU percentagem de desconto, não ambos.');
      return;
    }

    try {
      setSaving(true);
      const payload: any = {
        tipo_id: form.tipo_id,
        nome_entidade: form.nome_entidade?.trim() || null,
        titulo: form.titulo.trim(),
        descricao: form.descricao || null,
        valor: tipoSelecionado?.tipo === 'valor' ? Number(form.valor) : null,
        percentagem_desconto:
          tipoSelecionado?.tipo === 'desconto' ? Number(form.percentagem_desconto) : null,
        valor_minimo_compra: form.valor_minimo_compra ? Number(form.valor_minimo_compra) : null,
        quantidade_total: Number(form.quantidade_total),
        preco_venda: Number(form.preco_venda),
        data_validade: form.data_validade || null,
        termos_uso: form.termos_uso || null,
        imagem_logo: form.imagem_logo || null,
        status: form.status,
      };

      if (form.id) {
        await vouchersService.portalUpdateVoucher(form.id, payload);
      } else {
        await vouchersService.portalCreateVoucher(payload);
      }

      setShowForm(false);
      setForm(emptyForm);
      await loadAll();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar voucher');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v: PortalVoucher) => {
    if (v.quantidade_vendida > 0) {
      setError('Não é possível eliminar um voucher com vendas. Cancele-o em vez disso.');
      return;
    }
    if (!window.confirm(`Eliminar o voucher "${v.titulo}"?`)) return;
    try {
      await vouchersService.portalDeleteVoucher(v.id);
      await loadAll();
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar voucher');
    }
  };

  const openVendas = async (v: PortalVoucher) => {
    try {
      setVendasVoucher(v);
      setVendas([]);
      const data = await vouchersService.portalGetVendas(v.id, { limit: 100 });
      setVendas(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar vendas');
    }
  };

  const statusBadge = (status: string) => {
    const cfg: Record<string, string> = {
      rascunho: 'bg-gray-100 text-gray-800',
      ativo: 'bg-green-100 text-green-800',
      esgotado: 'bg-yellow-100 text-yellow-800',
      expirado: 'bg-red-100 text-red-800',
      cancelado: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${cfg[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const fmt = (n: number | null | undefined) =>
    n == null ? '-' : `${Number(n).toLocaleString('pt-CV')} ECV`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meus Vouchers</h1>
          <p className="text-gray-600">
            Cria e gere os vouchers vendidos aos clientes Pagali.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/portal/vouchers/scanner"
            className="px-4 py-2 bg-white border border-pagali-primary text-pagali-primary rounded-lg hover:bg-pagali-primary/5 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m0 14v1m8-8h1M3 12h1m15.364 6.364l.707.707M4.929 4.929l.707.707m12.728 0l.707-.707M4.929 19.071l.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Validar / Resgatar
          </Link>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Voucher
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 whitespace-pre-wrap">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">
            Fechar
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-semibold">{stats.total_vouchers}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Ativos</p>
            <p className="text-2xl font-semibold text-green-600">{stats.vouchers_ativos}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Vendas</p>
            <p className="text-2xl font-semibold">{stats.total_vendas}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Receita</p>
            <p className="text-2xl font-semibold text-pagali-primary">{fmt(stats.receita_total)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Resgates</p>
            <p className="text-2xl font-semibold">{stats.total_resgates}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
        >
          <option value="">Todos os estados</option>
          <option value="rascunho">Rascunho</option>
          <option value="ativo">Ativo</option>
          <option value="esgotado">Esgotado</option>
          <option value="expirado">Expirado</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor / Desconto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vouchers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    Ainda não tem vouchers. Clique em "Novo Voucher" para começar.
                  </td>
                </tr>
              ) : (
                vouchers.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{v.titulo}</div>
                      <div className="text-xs text-gray-500">{v.codigo}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{v.tipo_nome}</td>
                    <td className="px-6 py-4 text-sm">
                      {v.tipo_categoria === 'valor'
                        ? fmt(v.valor)
                        : v.percentagem_desconto
                        ? `${v.percentagem_desconto}% OFF`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{fmt(v.preco_venda)}</td>
                    <td className="px-6 py-4 text-sm">
                      {v.quantidade_vendida} / {v.quantidade_total}
                    </td>
                    <td className="px-6 py-4">{statusBadge(v.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button onClick={() => openVendas(v)} className="text-gray-600 hover:text-gray-900">
                        Vendas
                      </button>
                      <button onClick={() => openEdit(v)} className="text-pagali-primary hover:text-pagali-primary/80">
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(v)}
                        className="text-red-600 hover:text-red-900 disabled:text-gray-300"
                        disabled={v.quantidade_vendida > 0}
                        title={v.quantidade_vendida > 0 ? 'Voucher com vendas não pode ser eliminado' : ''}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">
                {form.id ? 'Editar Voucher' : 'Novo Voucher'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    required
                    value={form.tipo_id || ''}
                    onChange={(e) => setForm({ ...form, tipo_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  >
                    <option value="">Selecionar...</option>
                    {tipos.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nome} ({t.tipo})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={form.status || 'rascunho'}
                    onChange={(e) => setForm({ ...form, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  >
                    <option value="rascunho">Rascunho</option>
                    <option value="ativo">Ativo</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input
                  type="text"
                  required
                  minLength={3}
                  maxLength={200}
                  value={form.titulo}
                  onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  rows={2}
                  value={form.descricao || ''}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>

              {tipoSelecionado?.tipo === 'valor' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Facial (ECV) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.valor ?? ''}
                      onChange={(e) => setForm({ ...form, valor: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    />
                  </div>
                </div>
              )}

              {tipoSelecionado?.tipo === 'desconto' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentagem de Desconto (%) *</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.percentagem_desconto ?? ''}
                      onChange={(e) =>
                        setForm({ ...form, percentagem_desconto: e.target.value ? Number(e.target.value) : null })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Compra Mínima (ECV)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.valor_minimo_compra ?? ''}
                      onChange={(e) =>
                        setForm({ ...form, valor_minimo_compra: e.target.value ? Number(e.target.value) : null })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Entidade</label>
                <input
                  type="text"
                  maxLength={200}
                  value={form.nome_entidade || ''}
                  onChange={(e) => setForm({ ...form, nome_entidade: e.target.value })}
                  placeholder="Ex: Encosta Azul Guest House (aparece no voucher em vez do nome oficial)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
                <p className="text-xs text-gray-400 mt-1">Opcional — se vazio, usa o nome da empresa registada.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço de Venda (ECV) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={form.preco_venda}
                    onChange={(e) => setForm({ ...form, preco_venda: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade Disponível *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    step="1"
                    value={form.quantidade_total}
                    onChange={(e) => setForm({ ...form, quantidade_total: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Validade</label>
                  <input
                    type="date"
                    value={form.data_validade || ''}
                    onChange={(e) => setForm({ ...form, data_validade: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Termos de Utilização</label>
                <textarea
                  rows={3}
                  maxLength={5000}
                  value={form.termos_uso || ''}
                  onChange={(e) => setForm({ ...form, termos_uso: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL do Logo / Imagem</label>
                <input
                  type="text"
                  value={form.imagem_logo || ''}
                  onChange={(e) => setForm({ ...form, imagem_logo: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 disabled:opacity-60"
                >
                  {saving ? 'A guardar...' : form.id ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sales modal */}
      {vendasVoucher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Vendas — {vendasVoucher.titulo}</h2>
                <p className="text-sm text-gray-500">
                  {vendasVoucher.quantidade_vendida} de {vendasVoucher.quantidade_total} vendidos
                </p>
              </div>
              <button
                onClick={() => {
                  setVendasVoucher(null);
                  setVendas([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {vendas.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhuma venda ainda.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comprador</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor pago</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vendas.map((venda) => (
                      <tr key={venda.id}>
                        <td className="px-4 py-2 text-sm font-mono">{venda.codigo_unico}</td>
                        <td className="px-4 py-2 text-sm">{venda.comprador_nome || venda.beneficiario_nome || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {new Date(venda.data_compra).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                        </td>
                        <td className="px-4 py-2 text-sm">{fmt(venda.preco_pago)}</td>
                        <td className="px-4 py-2">{statusBadge(venda.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalVouchers;
